import { Suspense, lazy, useEffect, useRef, useState } from "react";
import { Slider } from "@/components/ui/slider";
import AnimatedNumber from "./motion/AnimatedNumber";
import { useInViewMount } from "./three/shared";

const BuyingScaleScene = lazy(() => import("./three/BuyingScaleScene"));

const TIERS = [
  { max: 625, label: "Modern Compact Property" },
  { max: 875, label: "Larger Family Home" },
  { max: 1125, label: "Premium Modern Home" },
  { max: 1375, label: "Luxury Property" },
  { max: 1500, label: "Ultra-Premium Property" },
];

const MARKS = ["$500K", "$750K", "$1M", "$1.25M", "$1.5M"];

export default function BuyingScale({ estimateHigh = 0 }) {
  const [val, setVal] = useState(850);
  const [showScene, setShowScene] = useState(false);
  const stageRef = useRef(null);
  const inView = useInViewMount(stageRef);

  useEffect(() => {
    setShowScene(!window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  }, []);

  const rawIdx = TIERS.findIndex((t) => val <= t.max);
  const tierIdx = rawIdx === -1 ? TIERS.length - 1 : rawIdx;
  const tier = TIERS[tierIdx];
  const estPct = Math.min(100, Math.max(0, ((estimateHigh - 500000) / 1000000) * 100));

  return (
    <div className="mt-16" data-testid="buying-scale">
      <div className="flex items-baseline justify-between mb-6">
        <p className="gold-label">Explore The Scale</p>
        <p className="text-[11px] text-ivory/35 tracking-wide">Illustrative properties · estimate only</p>
      </div>

      <div ref={stageRef} className="relative h-72 md:h-96 rounded-md overflow-hidden border border-white/10 bg-charcoal-700/40">
        {showScene && inView && (
          <Suspense fallback={null}>
            <BuyingScaleScene tier={tierIdx} />
          </Suspense>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal/90 via-transparent to-transparent pointer-events-none" />
        <div className="absolute bottom-0 inset-x-0 p-7 flex items-end justify-between">
          <div>
            <p data-testid="buying-scale-price" className="font-serif text-4xl">
              <AnimatedNumber value={val * 1000} className="gold-text-gradient" />
            </p>
            <p data-testid="buying-scale-label" className="mt-2 text-[11px] uppercase tracking-[0.22em] text-ivory/60">
              {tier.label}
            </p>
          </div>
          <p className="text-[11px] text-ivory/35 max-w-[170px] text-right leading-snug">
            Illustrative architectural styles only — not listings or current availability.
          </p>
        </div>
      </div>

      <div className="relative mt-8">
        {estimateHigh > 0 && (
          <div
            className="absolute -top-7 pointer-events-none transition-[left] duration-500"
            style={{ left: `calc(${estPct}% - 8px)` }}
            data-testid="buying-scale-estimate-marker"
          >
            <p className="text-[9px] tracking-[0.18em] text-gold whitespace-nowrap mb-1 -translate-x-1/4">YOUR ESTIMATE</p>
            <div className="w-2 h-2 rotate-45 bg-gold mx-auto" />
          </div>
        )}
        <Slider
          data-testid="buying-scale-slider"
          value={[val]}
          min={500}
          max={1500}
          step={25}
          onValueChange={([v]) => setVal(v)}
          className="[&_[role=slider]]:bg-gold [&_[role=slider]]:border-champagne [&_[role=slider]]:h-4 [&_[role=slider]]:w-4"
        />
        <div className="mt-3 flex justify-between">
          {MARKS.map((m) => (
            <span key={m} className="text-[10px] tracking-[0.14em] text-ivory/40">
              {m}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
